// /app/frontend/src/components/PricingModal.jsx
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, Loader2, Crown, Sparkles } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PricingModal = ({ open, onOpenChange, trigger }) => {
  const { user, isPro } = useAuth();
  const [loading, setLoading] = useState(null);

  const handleCheckout = async (priceId) => {
    setLoading(priceId);
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/billing/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ priceId })
      });

      if (!response.ok) {
        throw new Error('Failed to create checkout session');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const handleManageBilling = async () => {
    setLoading('portal');
    
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/billing/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to open billing portal');
      }

      const { url } = await response.json();
      window.location.href = url;
    } catch (error) {
      console.error('Portal error:', error);
      alert('Failed to open billing portal. Please try again.');
    } finally {
      setLoading(null);
    }
  };

  const freeFeatures = [
    '1 project',
    '1 pile per project',
    'Basic calculations',
    'Results preview'
  ];

  const proFeatures = [
    'Unlimited projects',
    'Unlimited piles',
    'PDF Technical Reports',
    'Sensitivity analysis',
    'Multi-pile overview',
    'Design scenarios',
    'Combined reports',
    'Priority support'
  ];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl" data-testid="pricing-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <Crown className="h-6 w-6 text-amber-500" />
            Upgrade to Pro
          </DialogTitle>
          <DialogDescription>
            {trigger 
              ? `This feature requires a Pro subscription: ${trigger}`
              : 'Unlock all features with Darkocean Pile Cap Pro'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 mt-6">
          {/* Free Plan */}
          <div className="border rounded-lg p-6 bg-muted/30">
            <div className="mb-4">
              <h3 className="font-semibold text-lg">Free</h3>
              <p className="text-3xl font-bold mt-2">$0<span className="text-base font-normal text-muted-foreground">/month</span></p>
            </div>
            
            <ul className="space-y-2 mb-6">
              {freeFeatures.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Check className="h-4 w-4 text-muted-foreground" />
                  {feature}
                </li>
              ))}
            </ul>

            <Button variant="outline" className="w-full" disabled>
              {isPro ? 'Downgrade' : 'Current Plan'}
            </Button>
          </div>

          {/* Pro Plan */}
          <div className="border-2 border-[#00284F] rounded-lg p-6 relative bg-[#00284F]/5">
            <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00284F]">
              <Sparkles className="h-3 w-3 mr-1" />
              RECOMMENDED
            </Badge>
            
            <div className="mb-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                Pro
                <Crown className="h-4 w-4 text-amber-500" />
              </h3>
              <p className="text-3xl font-bold mt-2">
                $99<span className="text-base font-normal text-muted-foreground">/month</span>
              </p>
              <p className="text-xs text-muted-foreground">billed annually ($1,188/year)</p>
            </div>
            
            <ul className="space-y-2 mb-6">
              {proFeatures.map((feature, i) => (
                <li key={i} className="flex items-center gap-2 text-sm">
                  <Check className="h-4 w-4 text-emerald-500" />
                  {feature}
                </li>
              ))}
            </ul>

            {isPro ? (
              <Button 
                variant="outline" 
                className="w-full"
                onClick={handleManageBilling}
                disabled={loading === 'portal'}
              >
                {loading === 'portal' ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Opening...</>
                ) : (
                  'Manage Billing'
                )}
              </Button>
            ) : (
              <div className="space-y-2">
                <Button 
                  className="w-full bg-[#00284F] hover:bg-[#00284F]/90"
                  onClick={() => handleCheckout('price_pro_annual')}
                  disabled={loading !== null}
                  data-testid="checkout-annual-btn"
                >
                  {loading === 'price_pro_annual' ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processing...</>
                  ) : (
                    <>Annual - $99/mo (Save $132)</>
                  )}
                </Button>
                <Button 
                  variant="outline"
                  className="w-full"
                  onClick={() => handleCheckout('price_pro_monthly')}
                  disabled={loading !== null}
                  data-testid="checkout-monthly-btn"
                >
                  {loading === 'price_pro_monthly' ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Processing...</>
                  ) : (
                    <>Monthly - $110/mo</>
                  )}
                </Button>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
